import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import MusicShell from "../fanHome/MusicShell";
import { ACCENT } from "../artistStudio/shared";
import { INK, MUTED } from "../fanHome/libraryParts";
import { purchaseApi, money, formatDate, apiErrorMessage } from "./fanMembershipApi";
import { receiptLink } from "../receipts/receiptApi";

// Purchase History: memberships, paid licences and tips, with the receipt and, for licences, a refund request. The server decides
// whether a refund is possible (policy, window, usage); this page shows what it answers.
const REFUND_STATUS = {
  pending_review: "Requested", info_requested: "More info needed", approved: "Approved", processing: "Processing",
  completed: "Refunded", refunded: "Refunded", rejected: "Declined", failed: "Failed"
};

function RefundDialog({ item, onClose, onDone }) {
  const [stage, setStage] = useState({ loading: true, check: null, error: "" });
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    purchaseApi.refundEligibility(item.refund.entitlement_id)
      .then((check) => live && setStage({ loading: false, check, error: "" }))
      .catch((e) => live && setStage({ loading: false, check: null, error: apiErrorMessage(e, "We could not check this licence. Please try again.") }));
    return () => { live = false; };
  }, [item]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const text = reason.trim();
    if (text.length < 10) { setError("Please tell us why in at least 10 characters."); return; }
    setSaving(true);
    setError("");
    try {
      await purchaseApi.refundRequest({ entitlement_id: item.refund.entitlement_id, reason: text });
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, "Your request could not be sent. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const { loading, check, error: checkError } = stage;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-label="Request a refund">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-bold" style={{ color: INK }}>Request a refund</h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>{item.item} · {money(item.amount, item.currency)}</p>
        {loading && <div className="flex justify-center py-8"><Loading /></div>}
        {!loading && checkError && <p className="mt-4 text-sm text-red-600" role="alert">{checkError}</p>}
        {!loading && check && !check.eligible && <p className="mt-4 text-sm text-gray-700" role="alert">{check.message || "This licence cannot be refunded."}</p>}
        {!loading && check?.eligible && (
          <form onSubmit={submit} className="mt-4">
            <label htmlFor="refund-reason" className="text-sm font-semibold" style={{ color: INK }}>Why do you want a refund?</label>
            <textarea id="refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={1000}
              className="mt-2 w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm" />
            {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
            <div className="mt-4 flex gap-3">
              <button type="submit" disabled={saving} className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>{saving ? "Sending…" : "Send request"}</button>
              <button type="button" onClick={onClose} className="rounded-full border-2 border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700">Cancel</button>
            </div>
          </form>
        )}
        {!loading && !(check?.eligible) && (
          <button type="button" onClick={onClose} className="mt-5 rounded-full border-2 border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700">Close</button>
        )}
      </div>
    </div>
  );
}

export default function PurchaseHistory() {
  const [state, setState] = useState({ loading: true, items: [], error: "" });
  const [refunding, setRefunding] = useState(null);
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const result = await purchaseApi.list();
      setState({ loading: false, items: result?.items || [], error: "" });
    } catch (e) {
      setState({ loading: false, items: [], error: apiErrorMessage(e, "Your purchases could not be loaded. Please try again.") });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refundCell = (it) => {
    if (it.refund?.status) return <span className="text-gray-600">{REFUND_STATUS[it.refund.status] || it.refund.status}</span>;
    if (it.refund?.can_request) {
      return <button type="button" onClick={() => setRefunding(it)} className="font-semibold text-red-600 hover:underline">Request</button>;
    }
    return <span className="text-gray-400">—</span>;
  };

  const { loading, items: all, error } = state;
  const TYPES_OF_FILTER = { subscriptions: ["membership"], licences: ["licence", "renewal"], tips: ["tip"] }; // a renewal is listed with licences
  const items = all.filter((it) => filter === "all" || (TYPES_OF_FILTER[filter] || []).includes(it.type));
  const chips = [["all", "All"], ["subscriptions", "Subscriptions"], ["licences", "Licences"], ["tips", "Tips"]];
  const subLine = (it) => `${formatDate(it.date)}${it.invoice_number ? ` · ${it.invoice_number}` : ""}`;
  return (
    <MusicShell>
      <h1 className="text-[28px] font-bold leading-tight" style={{ color: INK }}>Purchase History</h1>
      <p className="mt-1 text-sm" style={{ color: MUTED }}>Subscriptions, licences and tips, with receipts and refunds.</p>
      {notice && <p className="mt-4 max-w-4xl rounded-xl bg-green-50 px-4 py-3 text-sm" style={{ color: ACCENT }} role="status">{notice}</p>}
      <div className="mt-4 flex gap-2" role="tablist" aria-label="Filter purchases">
        {chips.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={filter === key} onClick={() => setFilter(key)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${filter === key ? "text-white" : "bg-[#e3f1e7]"}`}
            style={filter === key ? { backgroundColor: ACCENT } : { color: ACCENT }}>{label}</button>
        ))}
      </div>
      {loading && <div className="flex justify-center py-20" role="status" aria-label="Loading purchases"><Loading /></div>}
      {!loading && error && (
        <div className="py-12 max-w-md">
          <p className="text-gray-700 mb-4" role="alert">{error}</p>
          <button type="button" onClick={load} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="mt-6" style={{ color: MUTED }}>{all.length ? "Nothing in this list." : "No purchases yet."} <Link to="/fan-membership" className="font-semibold hover:underline" style={{ color: ACCENT }}>Choose a plan</Link></p>
      )}
      {!loading && !error && items.length > 0 && (
        <>
          <ul className="mt-5 max-w-xl space-y-3 md:hidden">
            {items.map((it) => (
              <li key={it.key} className="rounded-2xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <span className="font-bold" style={{ color: INK }}>{it.item}</span>
                  <span className="font-bold" style={{ color: INK }}>{money(it.amount, it.currency)}</span>
                </div>
                <p className="mt-1 text-sm" style={{ color: MUTED }}>{subLine(it)}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Link to={receiptLink(it.type, it.id)} className="rounded-full border border-gray-200 px-4 py-1.5 text-sm font-semibold" style={{ color: INK }}>Receipt</Link>
                  {it.refund?.can_request && <button type="button" onClick={() => setRefunding(it)} className="rounded-full border border-gray-200 px-4 py-1.5 text-sm font-semibold" style={{ color: INK }}>Request refund</button>}
                  {it.refund?.status && <span className="text-sm text-gray-600">Refund: {REFUND_STATUS[it.refund.status] || it.refund.status}</span>}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-6 hidden max-w-4xl overflow-x-auto rounded-3xl border border-gray-200 md:block">
            <table className="w-full text-left text-[15px]">
              <thead>
                <tr className="text-xs font-bold uppercase tracking-wide" style={{ color: ACCENT }}>
                  <th className="px-6 py-4">Date</th><th className="px-3 py-4">Item</th><th className="px-3 py-4">Amount</th><th className="px-3 py-4">Receipt</th><th className="px-3 py-4">Refund</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.key} className="border-t border-gray-200">
                    <td className="px-6 py-4 whitespace-nowrap" style={{ color: MUTED }}>{formatDate(it.date)}</td>
                    <td className="px-3 py-4" style={{ color: INK }}>{it.item}{it.invoice_number && <span className="block text-xs" style={{ color: MUTED }}>{it.invoice_number}</span>}</td>
                    <td className="px-3 py-4 font-semibold whitespace-nowrap" style={{ color: INK }}>{money(it.amount, it.currency)}</td>
                    <td className="px-3 py-4"><Link to={receiptLink(it.type, it.id)} className="font-semibold hover:underline" style={{ color: ACCENT }}>View</Link></td>
                    <td className="px-3 py-4">{refundCell(it)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {refunding && (
        <RefundDialog
          item={refunding}
          onClose={() => setRefunding(null)}
          onDone={() => { setRefunding(null); setNotice("Your refund request was sent. We will review it and email you the outcome."); load(); }}
        />
      )}
    </MusicShell>
  );
}
